import { apiClient } from './client';

export interface ProductOption {
  id: number;
  name: string;
  type: string;
  price: number;
  price_type: string;
  description?: string;
  is_active: boolean;
  sort_order: number;
  car_names?: string[];
  coverage_limit?: string;
  customer_burden?: string;
  created_at?: string;
  updated_at?: string;
}

// Simple in-memory cache to prevent duplicate API calls
const cache = new Map<string, { data: ProductOption[]; timestamp: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// Track ongoing requests to prevent duplicate simultaneous calls
const pendingRequests = new Map<string, Promise<ProductOption[]>>();

export const productOptionApi = {
  // Get all product options with caching
  getAllProductOptions: async (params?: {
    is_active?: boolean;
    type?: string;
  }): Promise<ProductOption[]> => {
    // Create cache key from params
    const cacheKey = JSON.stringify({ params });

    // Check if there's already a pending request for this key
    const pendingRequest = pendingRequests.get(cacheKey);
    if (pendingRequest) {
      return pendingRequest;
    }

    // Check cache
    const cached = cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      return cached.data;
    }

    // Make the actual API call
    const requestPromise = apiClient
      .get<ProductOption[]>('/product-options', { params })
      .then((response) => {
        // Cache the result
        cache.set(cacheKey, {
          data: response.data,
          timestamp: Date.now(),
        });
        // Remove from pending requests
        pendingRequests.delete(cacheKey);
        return response.data;
      })
      .catch((error) => {
        // Remove from pending requests on error
        pendingRequests.delete(cacheKey);
        throw error;
      });

    // Store the pending request
    pendingRequests.set(cacheKey, requestPromise);

    return requestPromise;
  },

  // Get product option by ID
  getProductOptionById: async (id: number): Promise<ProductOption> => {
    const response = await apiClient.get<ProductOption>(`/product-options/${id}`);
    return response.data;
  },
};
